import React, { useState, useEffect } from 'react';
import {
    AppBar, Divider, IconButton, Menu, MenuItem,
    Stack, Toolbar, Typography, useMediaQuery, useTheme, Box
} from '@mui/material';
import { useUser } from './userContext'
import { useNavigate } from 'react-router-dom';
import { NavItem } from './nav-item';
import PeopleIcon from '@mui/icons-material/People';
import HomeIcon from '@mui/icons-material/Home';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SettingsIcon from '@mui/icons-material/Settings';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PermDeviceInformationIcon from '@mui/icons-material/PermDeviceInformation';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import BugReportIcon from '@mui/icons-material/BugReport';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArchiveIcon from '@mui/icons-material/Archive';
import BarChartIcon from '@mui/icons-material/BarChart';
import SummarizeIcon from '@mui/icons-material/Summarize';

import { BugTrackerButton } from '../components/BugTracker';
import { NotificationsButton } from '../components/NotificationsButton';
import { CodiChatButton } from '../components/CodiChat';

import { usePermissions } from './permissionContext';
import { useConfig } from '../components/configContext';
import { useThemeContext, THEME_DARK } from '../theme/ThemeContext';

function TopMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const { user, logout } = useUser();
    const navigate = useNavigate();
    const { hasFeature } = usePermissions();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [navMenuAnchor, setNavMenuAnchor] = useState(null);
    const [subMenuAnchor, setSubMenuAnchor] = useState(null);
    const [logoSet, setLogoSet] = useState('coderis');
    const [codiActivated, setCodiActivated] = useState(false);

    const { getConfigValue } = useConfig();
    const hasCodiUser = hasFeature('CODI-USER');
    const codiFeatureOn = getConfigValue('codi_enabled', false);
    const { currentTheme } = useThemeContext();

    const handleSubMenuOpen = (event) => {
        setSubMenuAnchor(event.currentTarget);
    };

    const handleSubMenuClose = () => {
        setSubMenuAnchor(null);
    };

    const handleNavMenuOpen = (event) => {
        setNavMenuAnchor(event.currentTarget);
    };

    const handleNavMenuClose = () => {
        setNavMenuAnchor(null);
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSettings = () => {
        setAnchorEl(null);
        navigate('/settings');
    };

    const handleLogout = () => {
        console.log('Logout: ' + user.username);
        logout();
        navigate('/login');
    };

    // NEU: Navigation zu den Benachrichtigungseinstellungen
    // eslint-disable-next-line
    const handleNotificationsSettings = () => {
        setAnchorEl(null);
        navigate('/notification-preferences');
    };

    // Fetch user settings (includes tenant-specific logo configuration)
    useEffect(() => {
        const fetchUserSettings = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URI}/api/user-settings`, {
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data?.settings?.logoSet) {
                        setLogoSet(data.settings.logoSet);
                    }
                }
            } catch (error) {
                console.error('Error fetching user settings:', error);
            }
        };

        if (user) {
            fetchUserSettings();
        }
    }, [user]);

    // Codi: UI-Schalter aus /api/codi/config nur laden, wenn global aktiv und Feature vorhanden
    useEffect(() => {
        let aborted = false;
        async function loadCodiConfig() {
            try {
                setCodiActivated(false);
                if (!user || !user.api_key) return;
                if (!codiFeatureOn || !hasCodiUser) return;
                const resp = await fetch(`${process.env.REACT_APP_API_URI}/api/codi/config`, {
                    method: 'GET',
                    headers: {
                        'Authorization': user.api_key,
                        'Content-Type': 'application/json'
                    }
                });
                if (!resp.ok) return;
                const data = await resp.json();
                if (!aborted) setCodiActivated(!!data?.data?.enabled);
            } catch (e) {
                if (!aborted) setCodiActivated(false);
            }
        }
        loadCodiConfig();
        return () => { aborted = true; };
    }, [user, codiFeatureOn, hasCodiUser]);

    // Determine which logos to use based on logoSet configuration
    const getLogoImage = () => {
        if (currentTheme === THEME_DARK) {
            return 'coderis_logo_white.svg';
        }
        if (logoSet === 'drk-bos') {
            return 'drk-logo.jpg';
        }
        return 'coderis_Logo.svg';
    };

    const getWordmarkImage = () => {
        if (currentTheme === THEME_DARK) {
            return null;
        }
        if (logoSet === 'drk-bos') {
            return 'DRK_RD_BOS.png';
        }
        return null;
    };

    // Einheitliches SX für Header-Buttons in allen Themes (Farben per Theme-Tokens)
    const headerIconButtonSx = {
        p: 0.75,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.selected',
        '&:hover': { bgcolor: 'action.hover' },
        mx: 1,
    };

    return (
        <AppBar position="static" color="default">
            <Toolbar sx={{ alignItems: 'center' }}>
                {/* Logo */}
                <img src={getLogoImage()} alt="Logo" style={{ height: isSmallScreen ? '50px' : '65px', padding: '5px' }} />
                {!isSmallScreen && getWordmarkImage() && (
                    <div style={{ marginLeft: '2px', marginBottom: '4px' }}>
                        <img src={getWordmarkImage()} alt="Logo" />
                    </div>
                )}

                <div style={{ flexGrow: 1 }}></div>

                {user && (
                    <>
                        {/* Navigation Links Container */}
                        {isSmallScreen ? (
                            <>
                                <IconButton color="inherit" onClick={handleNavMenuOpen}>
                                    <MenuIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={navMenuAnchor}
                                    open={Boolean(navMenuAnchor)}
                                    onClose={handleNavMenuClose}
                                >
                                    {(user?.username) && (
                                        <>
                                            <MenuItem onClick={handleNavMenuClose}>
                                                <NavItem key="Dashboard" icon={<HomeIcon />} href="/dashboard" title="Startseite" />
                                            </MenuItem>
                                            <MenuItem onClick={handleNavMenuClose}>
                                                <NavItem key="Bestellungen" icon={<AirportShuttleIcon />} href="/transportlist" title="Bestellungen" />
                                            </MenuItem>
                                            {(hasFeature('STATISTICS-DASHBOARD')) && (
                                                <MenuItem onClick={handleNavMenuClose}>
                                                    <NavItem key="Statistiken" icon={<BarChartIcon />} href="/statistics" title="Statistiken" />
                                                </MenuItem>
                                            )}
                                            {(() => {
                                                const canAccessAdmin = (
                                                    user?.role === 'admin' ||
                                                    user?.role === 'superadmin' ||
                                                    hasFeature('ADMIN-DASHBOARD')
                                                );
                                                if (canAccessAdmin) {
                                                    return (
                                                        <>
                                                            {user?.role === 'superadmin' && (
                                                                <>
                                                                    <MenuItem
                                                                        onClick={() => { handleNavMenuClose(); navigate('/setuppage'); }}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.08)'
                                                                        }}
                                                                    >
                                                                        <DatasetLinkedIcon fontSize="small" />
                                                                        Setup (Superadmin)
                                                                    </MenuItem>
                                                                    <MenuItem
                                                                        onClick={() => { handleNavMenuClose(); navigate('/system-report'); }}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.08)'
                                                                        }}
                                                                    >
                                                                        <SummarizeIcon fontSize="small" />
                                                                        Systembericht
                                                                    </MenuItem>
                                                                </>
                                                            )}
                                                            <MenuItem
                                                                onClick={() => { handleNavMenuClose(); navigate('/admin'); }}
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                                            >
                                                                <DashboardIcon fontSize="small" />
                                                                Administration
                                                            </MenuItem>
                                                        </>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </>
                                    )}
                                </Menu>
                            </>
                        ) : (
                            <>
                                <Stack direction="row" spacing={1}>
                                    {(user?.username) && (
                                        <>
                                            <NavItem key="Dashboard" icon={<HomeIcon />} href="/dashboard" title="Startseite" />

                                            {(user?.role === 'admin' || hasFeature('GRAFISCHE-PLANUNG')) && (
                                                <NavItem key="Disposition" icon={<EventNoteIcon />} href="/timeline" title="Planungsübersicht" />
                                            )}
                                            {/*
                                            {(hasFeature('KARTENANSICHT-FAHRZEUGE')) && (
                                                <NavItem key="Kartenansicht" icon={<MapIcon />} href="/vehicles/map" title="Kartenansicht" />
                                            )}
                                                */}
                                            {(hasFeature('BESTELLUNGEN')) && (
                                                <NavItem key="Bestellungen" icon={<AirportShuttleIcon />} href="/transportlist" title="Bestellungen" />
                                            )}
                                            {(hasFeature('TRANSPORT-ARCHIVE')) && (
                                                <NavItem key="Archiv" icon={<ArchiveIcon />} href="/transport-archive" title="Archiv" />
                                            )}
                                            {(hasFeature('STATISTICS-DASHBOARD')) && (
                                                <NavItem key="Statistiken" icon={<BarChartIcon />} href="/statistics" title="Statistiken" />
                                            )}
                                            {(user?.role === 'admin' || hasFeature('FLOTTENVERWALTUNG')) && (
                                                <NavItem key="Flottenverwaltung" icon={<EmojiTransportationIcon />} href="/flottenverwaltung" title="Flottenverwaltung" />
                                            )}
                                        </>
                                    )}
                                    {(() => {
                                        return (
                                            user?.role === 'admin' ||
                                            user?.role === 'superadmin' ||
                                            hasFeature('ADMIN-DASHBOARD')
                                        );
                                    })() && (
                                            <>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MenuItem onClick={handleSubMenuOpen}>
                                                        <SettingsIcon />
                                                        <Typography variant="body2" sx={{
                                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255, 0.08)' : 'rgba(0,0,0, 0.04)',
                                                            borderRadius: 1,
                                                            marginLeft: 1,
                                                            justifyContent: 'flex-start',
                                                            textAlign: 'left',
                                                            textTransform: 'none',
                                                            width: '100%',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            Administration
                                                        </Typography>
                                                        <ExpandMoreIcon />
                                                    </MenuItem>
                                                    <Menu
                                                        anchorEl={subMenuAnchor}
                                                        open={Boolean(subMenuAnchor)}
                                                        onClose={handleSubMenuClose}
                                                    >
                                                        {(user?.role === 'superadmin') && (
                                                            <>
                                                                <MenuItem
                                                                    onClick={handleSubMenuClose}
                                                                    sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.08)' }}
                                                                >
                                                                    <NavItem key="Setup" icon={<DatasetLinkedIcon />} href="/setuppage" title="Setup (Superadmin)" />
                                                                </MenuItem>
                                                                <MenuItem
                                                                    onClick={handleSubMenuClose}
                                                                    sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.08)' }}
                                                                >
                                                                    <NavItem key="SystemReport" icon={<SummarizeIcon />} href="/system-report" title="Systembericht" />
                                                                </MenuItem>
                                                                <Divider />
                                                            </>
                                                        )}
                                                        <MenuItem onClick={handleSubMenuClose}>
                                                            <NavItem key="AdminNavigator" icon={<DashboardIcon />} href="/admin" title="Admin Dashboard" />
                                                        </MenuItem>
                                                        <Divider />
                                                        {(hasFeature('ADMIN-STAMMDATEN')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Stammdaten" icon={<DatasetLinkedIcon />} href="/stammdaten" title="Stammdaten" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-SONSTIGES')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Zeitkorridore" icon={<AccessTimeIcon />} href="/timeSlotManagement" title="Zeitkorridore" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-ROLLEN-UND-RECHTE')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="RollenRechte" icon={<ChecklistRtlIcon />} href="/role-management" title="Rollen&Rechte" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-BENUTZERVERWALTUNG')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Benutzer" icon={<PeopleIcon />} href="/userspage" title="Benutzer" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-KOMMUNIKATOR')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Kommunikator" icon={<ChatBubbleIcon />} href="/admin-communicator" title="Kommunikator-Verwaltung" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-MAIL-TEMPLATES')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Mail-Templates" icon={<EmailIcon />} href="/mail-templates" title="E-Mail-Templates" />
                                                            </MenuItem>
                                                        )}
                                                        <Divider />
                                                        {(hasFeature('ADMIN-SONSTIGES')) && (
                                                            <>
                                                                <MenuItem onClick={handleSubMenuClose}>
                                                                    <NavItem key="Institutionen" icon={<ApartmentIcon />} href="/institutions" title="Institutionen" />
                                                                </MenuItem>
                                                                <MenuItem onClick={handleSubMenuClose}>
                                                                    <NavItem key="Infektionen" icon={<HealthAndSafetyIcon />} href="/infections" title="Infektionen" />
                                                                </MenuItem></>
                                                        )}
                                                        {(hasFeature('ADMIN-PARAMETER')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Paramter" icon={<HealthAndSafetyIcon />} href="/params" title="Parameter" />
                                                            </MenuItem>
                                                        )}
                                                        {(hasFeature('ADMIN-BUG-VERWALTUNG')) && (
                                                            <MenuItem onClick={handleSubMenuClose}>
                                                                <NavItem key="Bugs" icon={<BugReportIcon />} href="/bug-reports" title="Bug-Reports" />
                                                            </MenuItem>
                                                        )}
                                                        {(user?.role === 'admin') && (
                                                            <>
                                                                <MenuItem onClick={handleSubMenuClose}>
                                                                    <NavItem key="LogFiles" icon={<FindInPageIcon />} href="/logs" title="Log-Files" />
                                                                </MenuItem>
                                                                <Divider />
                                                            </>
                                                        )}
                                                        <MenuItem onClick={handleSubMenuClose}>
                                                            <NavItem key="ReleaseNotes" icon={<PermDeviceInformationIcon />} href="/releasenotes" title="Release-Notes" />
                                                        </MenuItem>
                                                    </Menu>
                                                </Box>
                                            </>
                                        )}
                                </Stack>

                                <div style={{ flexGrow: 1 }}></div>


                                {/* Codi Chat Button */}
                                {hasCodiUser && codiFeatureOn && codiActivated && (
                                    <CodiChatButton position="inline" size="medium" inHeader={true} sx={headerIconButtonSx} />
                                )}
                                {/* Bug Tracker Button hinzugefügt */}
                                {((user?.role === 'superadmin') || hasFeature('BUG-TRACKER')) && (
                                    <BugTrackerButton position="inline" size="medium" inHeader={true} sx={headerIconButtonSx} />
                                )}
                                {((user?.role === 'superadmin') || hasFeature('KOMMUNIKATION-BENACHRICHTIGUNGEN')) && (
                                    <NotificationsButton position="inline" size="medium" inHeader={true} sx={headerIconButtonSx} />
                                )}
                            </>
                        )}
                    </>
                )}

                {/* Profil-Avatar mit Menü */}
                {user && (
                    <div>
                        <IconButton
                            edge="end"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleMenu}
                            color="inherit"
                            sx={headerIconButtonSx}
                        >
                            <AccountCircleIcon color="primary" sx={{ fontSize: 24 }} />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            {/* Benutzerinformationen (Benutzername und Rolle) */}
                            <div style={{ marginLeft: '12px', marginBottom: '12px' }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    {user && user.username}
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    Rolle: {user && (user.role === 'poweruser' ? 'Disponent' : user.role)}
                                </Typography>
                            </div>
                            <Divider />
                            <MenuItem onClick={handleSettings}>
                                <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
                                Einstellungen
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>Logout</MenuItem>
                        </Menu>
                    </div>
                )}
            </Toolbar>
        </AppBar >
    );
}

export default TopMenu;
